import React, { useState } from "react";
import { z } from "zod";
import { Modal } from "@/components/common/Modal";
import { Input } from "@/components/common/Input";
import { Button } from "@/components/common/Button";
import { authService } from "@/services/authService";
import { useNotification } from "@/hooks/useNotification";

const changePasswordSchema = z
  .object({
    old_password: z.string().min(1, "A senha atual é obrigatória"),
    new_password: z
      .string()
      .min(8, "A nova senha deve ter no mínimo 8 caracteres")
      .max(72, "A nova senha deve ter no máximo 72 caracteres"),
    confirm_password: z.string().min(1, "Confirmação de senha obrigatória"),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "A nova senha e confirmação não conferem",
    path: ["confirm_password"],
  });

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose }) => {
  const { notify } = useNotification();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = changePasswordSchema.safeParse({
      old_password: oldPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        const field = issue.path[0] as string;
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await authService.changePassword({
        old_password: oldPassword,
        new_password: newPassword,
      });
      notify("Senha alterada com sucesso! Sessões anteriores foram invalidadas.", "success");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      onClose();
    } catch (err: any) {
      notify(err.message || "Erro ao alterar a senha", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Alterar Senha do Administrador">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          type="password"
          label="Senha Atual"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          error={errors.old_password}
          required
          autoComplete="current-password"
        />
        <Input
          type="password"
          label="Nova Senha"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          error={errors.new_password}
          helperText="Mínimo de 8 e máximo de 72 caracteres (OWASP ASVS)"
          required
          autoComplete="new-password"
        />
        <Input
          type="password"
          label="Confirmar Nova Senha"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={errors.confirm_password}
          required
          autoComplete="new-password"
        />

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" isLoading={isSubmitting}>
            Atualizar Senha
          </Button>
        </div>
      </form>
    </Modal>
  );
};
